import { useState, useCallback, useEffect, useRef } from 'react'
import { Fingerprint, Download, UserPlus } from 'lucide-react'
import './UnlockPrompt.css'

interface UnlockPromptProps {
    /** Default: unlock local vault via WebAuthn assertion */
    onUnlock: () => Promise<boolean>
    onDismiss: () => void
    /** PWA recovery variant — shows server recovery + import/create */
    variant?: 'local' | 'pwa'
    /** Triggered when user taps "Sign in with passkey" in PWA variant */
    onRecoverFromServer?: () => Promise<boolean>
    /** Triggered when user taps "Import backup key" in PWA variant */
    onImport?: () => void
    /** Triggered when user taps "Create new profile" in PWA variant */
    onCreate?: () => void
    /** When false, hides self-signup entry points. */
    signupEnabled?: boolean
    /** Online status — when false, disables server recovery in PWA variant */
    online?: boolean
    /** When true, shows "Continue as guest" and enables backdrop dismiss (desktop browser). */
    dismissable?: boolean
}

export default function UnlockPrompt({
    onUnlock,
    onDismiss,
    variant = 'local',
    onRecoverFromServer,
    onImport,
    onCreate,
    signupEnabled = false,
    online = true,
    dismissable = false,
}: UnlockPromptProps) {
    const [unlocking, setUnlocking] = useState(false)
    const [recovering, setRecovering] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleSignIn = useCallback(async () => {
        setUnlocking(true)
        setError(null)
        try {
            const ok = await onUnlock()
            if (!ok) {
                // User cancelled the WebAuthn dialog — dismiss to guest mode
                onDismiss()
            }
            // If ok === true, identity:changed will transition to real workspace
        } catch {
            onDismiss()
        } finally {
            setUnlocking(false)
        }
    }, [onUnlock, onDismiss])

    const handleRecoverFromServer = useCallback(async () => {
        if (!onRecoverFromServer) return
        setRecovering(true)
        setError(null)
        try {
            const ok = await onRecoverFromServer()
            if (!ok) {
                setError(signupEnabled
                    ? 'No existing profile found — create one below or import a backup key.'
                    : 'No existing profile found — import a backup key or try another passkey.')
            }
            // If ok === true, identity:changed will transition to real workspace
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not reach server — check your connection and try again.')
        } finally {
            setRecovering(false)
        }
    }, [onRecoverFromServer, signupEnabled])

    // Auto-trigger passkey picker when PWA sign-in screen mounts.
    // iOS 17.4+ removed the user-gesture requirement (rate-limited instead).
    // Chrome/Edge/Windows never required it. Button remains as fallback.
    const autoTriggered = useRef(false)
    useEffect(() => {
        if (variant !== 'pwa' || !onRecoverFromServer || !online) return
        if (autoTriggered.current) return
        autoTriggered.current = true
        const timer = setTimeout(() => handleRecoverFromServer(), 300)
        return () => clearTimeout(timer)
    }, [variant, onRecoverFromServer, online, handleRecoverFromServer])

    const busy = unlocking || recovering
    const canCreateProfile = signupEnabled && onCreate

    // ── PWA Sign-in variant ─────────────────────────────────
    if (variant === 'pwa') {
        return (
            <div className="unlock-prompt-backdrop" onClick={dismissable ? onDismiss : undefined}>
                <div className="unlock-prompt-card" onClick={e => e.stopPropagation()}>
                    <div className="unlock-prompt-icon">🔐</div>
                    <h2 className="unlock-prompt-title">Welcome to SecureBlob</h2>
                    <p className="unlock-prompt-subtitle">
                        Sign in to access your<br />workspaces from this app.
                    </p>

                    {error && (
                        <div className="unlock-prompt-error" role="alert">{error}</div>
                    )}

                    {recovering && (
                        <div className="unlock-prompt-loading">
                            <div className="unlock-prompt-spinner" />
                            <span>Restoring your profile…</span>
                        </div>
                    )}

                    {!online && !recovering && (
                        <div className="unlock-prompt-offline-hint">Connect to the internet to sign in</div>
                    )}

                    <button
                        className="unlock-prompt-btn-primary"
                        onClick={handleRecoverFromServer}
                        disabled={busy || !online}
                        id="pwa-sign-in-btn"
                    >
                        <Fingerprint size={18} />
                        {recovering ? 'Signing in…' : 'Sign in with passkey'}
                    </button>
                    <button
                        className="unlock-prompt-btn-secondary"
                        onClick={onImport}
                        disabled={busy}
                        id="pwa-import-btn"
                    >
                        <Download size={15} />
                        Import backup key
                    </button>
                    {canCreateProfile && (
                        <button
                            className="unlock-prompt-btn-tertiary"
                            onClick={onCreate}
                            disabled={busy}
                            id="pwa-create-btn"
                        >
                            <UserPlus size={15} />
                            Create new profile
                        </button>
                    )}
                    {dismissable && (
                        <button
                            className="unlock-prompt-btn-skip"
                            onClick={onDismiss}
                            disabled={busy}
                        >
                            Continue as guest
                        </button>
                    )}
                </div>
            </div>
        )
    }

    // ── Default local unlock variant ────────────────────────
    return (
        <div className="unlock-prompt-backdrop" onClick={onDismiss}>
            <div className="unlock-prompt-card" onClick={e => e.stopPropagation()}>
                <div className="unlock-prompt-icon">👋</div>
                <h2 className="unlock-prompt-title">Welcome back</h2>
                <p className="unlock-prompt-subtitle">
                    You already have a profile on this device.<br />
                    Sign in to access your workspaces.
                </p>
                <button
                    className="unlock-prompt-btn-primary"
                    onClick={handleSignIn}
                    disabled={unlocking}
                >
                    <Fingerprint size={18} />
                    {unlocking ? 'Signing in…' : 'Sign in with passkey'}
                </button>
                <button
                    className="unlock-prompt-btn-secondary"
                    onClick={onDismiss}
                    disabled={unlocking}
                >
                    Continue as guest
                </button>
            </div>
        </div>
    )
}
